/**
 * Tweakwise item IDs are not Shopware product IDs.
 *
 * The feed (richardhaeser/sw-tweakwise) exports every item as
 * `sprintf('%x-%s', crc32(domainId), productId)`, so an `itemno` coming back from
 * the navigation, search or recommendations API looks like
 * `f7d06c94-00031e11a240e68bf52c5b25c532bed7`. Feeding that straight into a
 * Shopware `id` filter matches nothing — strip the prefix first.
 *
 * The store-api already returns Shopware IDs (NdTweakwise translates them
 * server-side); these helpers are only needed where the frontend talks to the
 * Tweakwise gateway itself.
 */

// {crc32 as hex, leading zeros dropped by %x}-{32 char Shopware UUID}
const TWEAKWISE_ITEM_ID = /^[0-9a-f]{1,8}-([0-9a-f]{32})$/i;

interface TweakwiseItem {
    itemno?: string;
    attributes?: Array<{ name?: string; values?: string[] }>;
}

// The feed exports the Shopware ID as the `sw-id` attribute; Tweakwise strips the
// dash, so it arrives as `swid`. Match either spelling.
function swIdAttribute(item?: TweakwiseItem | null): string | undefined {
    const attribute = item?.attributes?.find(a => a?.name?.toLowerCase().replace(/[-_]/g, '') === 'swid');
    const value = attribute?.values?.[0];

    return value && /^[0-9a-f]{32}$/i.test(value) ? value : undefined;
}

/**
 * The Shopware product ID inside a Tweakwise `itemno`.
 * Values that are already a bare Shopware ID are returned unchanged.
 */
export function shopwareIdFromItemNo(itemNo?: string | null): string | undefined {
    if (!itemNo) return undefined;

    const match = itemNo.match(TWEAKWISE_ITEM_ID);
    if (match) return match[1];

    // Bare 32 char id (older feeds, or an id that was already converted)
    return /^[0-9a-f]{32}$/i.test(itemNo) ? itemNo : undefined;
}

/**
 * The Shopware product ID of a Tweakwise item.
 *
 * Prefers the `sw-id` attribute the feed exports (authoritative, and unaffected
 * by any future change to the item ID format); falls back to the `itemno`.
 */
export function shopwareIdFromItem(item?: TweakwiseItem | null): string | undefined {
    return swIdAttribute(item) ?? shopwareIdFromItemNo(item?.itemno);
}

/**
 * Shopware product IDs for a list of Tweakwise items, in Tweakwise's order.
 * Items without a usable ID are dropped.
 */
export function shopwareIdsFromItems(items?: TweakwiseItem[] | null): string[] {
    return (items ?? []).map(shopwareIdFromItem).filter((id): id is string => !!id);
}

/**
 * The Tweakwise item ID for a Shopware product ID.
 *
 * `prefix` is the hex CRC32 of the sales channel domain the feed was generated
 * for — the part before the dash of any `itemno` in a Tweakwise response. The
 * frontend cannot compute it, so pass one taken from a response.
 */
export function tweakwiseItemNo(shopwareId: string, prefix: string): string {
    return `${prefix}-${shopwareId}`;
}

/**
 * The CRC32 prefix used by an existing Tweakwise response.
 */
export function itemNoPrefix(itemNo?: string | null): string | undefined {
    const match = itemNo?.match(TWEAKWISE_ITEM_ID);
    return match ? itemNo!.slice(0, itemNo!.indexOf('-')) : undefined;
}
